// src/components/ApiMetrics.js
import React, { useEffect, useState } from 'react';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import '../styles/ApiMetrics.css';

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const ApiMetrics = () => {
  const [cacheStats, setCacheStats] = useState({
    hits: 0,
    misses: 0,
    size: 0,
    hitRate: 0,
  });
  const [hasData, setHasData] = useState(false);
  const [performanceHistory, setPerformanceHistory] = useState([]);

  useEffect(() => {
    loadCacheStats();
    const interval = setInterval(loadCacheStats, 5000); // Refresh toutes les 5s
    return () => clearInterval(interval);
  }, []);

  const loadCacheStats = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/cache/stats', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Parser le hitRate (peut être une chaîne avec % ou un nombre)
        const hits = Number(data.hits) || 0;
        const misses = Number(data.misses) || 0;
        const total = hits + misses;
        
        let hitRate = 0;
        if (typeof data.hitRate === 'string') {
          // Si c'est une chaîne (ex: "75.50%"), on retire le % et on parse
          hitRate = parseFloat(data.hitRate.replace('%', '')) || 0;
        } else if (Number.isFinite(data.hitRate)) {
          hitRate = Number(data.hitRate);
        } else if (total > 0) {
          // Calculer manuellement si nécessaire
          hitRate = (hits / total) * 100;
        }
        
        setCacheStats({
          hits: hits,
          misses: misses,
          size: Number(data.size) || 0,
          hitRate: hitRate,
        });
        
        setHasData(true);

        // Ajouter au historique de performance
        setPerformanceHistory((prev) => {
          const newHistory = [
            ...prev,
            { time: new Date().getTime(), hitRate: hitRate },
          ].slice(-20); // Garder les 20 dernières mesures
          return newHistory;
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des stats cache:', error);
    }
  };

  // Graphique circulaire pour le cache
  const cacheChartData = {
    labels: ['Hits', 'Misses'],
    datasets: [
      {
        data: [cacheStats.hits, cacheStats.misses],
        backgroundColor: ['#10B981', '#EF4444'],
        borderWidth: 0,
        cutout: '75%',
      },
    ],
  };

  const cacheChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderWidth: 0,
        displayColors: false,
      },
    },
  };

  // Graphique de performance
  const performanceChartData = {
    labels: performanceHistory.map(() => ''),
    datasets: [
      {
        data: performanceHistory.map((p) => p.hitRate),
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  };

  const performanceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        displayColors: false,
        callbacks: {
          label: (context) => `${context.parsed.y.toFixed(1)}%`,
        },
      },
    },
    scales: {
      x: { display: false },
      y: {
        display: false,
        min: 0,
        max: 100,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  const totalRequests = cacheStats.hits + cacheStats.misses;

  return (
    <div className="api-metrics-container">
      <h2 className="metrics-title">
        <span className="metrics-icon">⚡</span>
        Performance API
      </h2>

      <div className="metrics-grid">
        {/* Cache Hit Rate */}
        <div className="metric-card glass">
          <div className="metric-chart-container">
            <Doughnut data={cacheChartData} options={cacheChartOptions} />
            <div className="metric-center-value">
              <div className="metric-percentage">
                {hasData ? (cacheStats.hitRate || 0).toFixed(1) : '0.0'}%
              </div>
              <div className="metric-label">Hit Rate</div>
            </div>
          </div>
          <div className="metric-stats">
            <div className="metric-stat-item">
              <span className="stat-label">Hits</span>
              <span className="stat-value success">{cacheStats.hits.toLocaleString()}</span>
            </div>
            <div className="metric-stat-item">
              <span className="stat-label">Misses</span>
              <span className="stat-value danger">{cacheStats.misses.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Performance History */}
        <div className="metric-card glass">
          <div className="metric-header">
            <h3>Évolution du cache</h3>
            <span className="metric-badge">Temps réel</span>
          </div>
          <div className="metric-chart-area">
            {performanceHistory.length > 0 ? (
              <Line data={performanceChartData} options={performanceChartOptions} />
            ) : (
              <div className="metric-empty">Chargement des données...</div>
            )}
          </div>
        </div>

        {/* Statistiques rapides */}
        <div className="metric-card glass">
          <div className="metric-header">
            <h3>Statistiques</h3>
          </div>
          <div className="quick-stats">
            <div className="quick-stat-item">
              <div className="quick-stat-icon">📊</div>
              <div className="quick-stat-content">
                <div className="quick-stat-label">Requêtes totales</div>
                <div className="quick-stat-value">{totalRequests.toLocaleString()}</div>
              </div>
            </div>
            <div className="quick-stat-item">
              <div className="quick-stat-icon">💾</div>
              <div className="quick-stat-content">
                <div className="quick-stat-label">Taille du cache</div>
                <div className="quick-stat-value">{cacheStats.size.toLocaleString()}</div>
              </div>
            </div>
            <div className="quick-stat-item">
              <div className="quick-stat-icon">
                {(cacheStats.hitRate || 0) >= 80 ? '🟢' : (cacheStats.hitRate || 0) >= 50 ? '🟡' : '🔴'}
              </div>
              <div className="quick-stat-content">
                <div className="quick-stat-label">État du cache</div>
                <div className="quick-stat-value">
                  {(cacheStats.hitRate || 0) >= 80
                    ? 'Excellent'
                    : (cacheStats.hitRate || 0) >= 50
                    ? 'Bon'
                    : hasData ? 'À optimiser' : 'En attente'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiMetrics;

